<template>
  <div class="json-export-import-manager">
    <!-- Header with mode toggle and actions -->
    <div class="json-export-import-manager__header">
      <div class="json-export-import-manager__mode-toggle">
        <v-btn-toggle
          v-model="currentMode"
          mandatory
          dense
          class="elevation-0"
        >
          <v-btn
            value="export"
            small
            :disabled="disabled"
            data-testid="export-mode-btn"
          >
            <v-icon left small>mdi-export</v-icon>
            Export
          </v-btn>
          <v-btn
            value="import"
            small
            :disabled="disabled"
            data-testid="import-mode-btn"
          >
            <v-icon left small>mdi-import</v-icon>
            Import
          </v-btn>
        </v-btn-toggle>
      </div>

      <div class="json-export-import-manager__actions">
        <template v-if="currentMode === 'export'">
          <v-btn
            small
            outlined
            :disabled="disabled || !hasValidJson"
            @click="copyToClipboard"
            data-testid="copy-btn"
          >
            <v-icon left small>mdi-content-copy</v-icon>
            Copy
          </v-btn>
          <v-btn
            small
            color="primary"
            :disabled="disabled || !hasValidJson"
            @click="downloadJson"
            data-testid="download-btn"
          >
            <v-icon left small>mdi-download</v-icon>
            Download
          </v-btn>
        </template>
        <template v-else>
          <v-btn
            small
            outlined
            :disabled="disabled"
            @click="clearEditor"
            data-testid="clear-btn"
          >
            <v-icon left small>mdi-delete</v-icon>
            Clear
          </v-btn>
          <v-btn
            small
            color="primary"
            :disabled="disabled || !hasValidJson"
            @click="importConfiguration"
            data-testid="import-btn"
          >
            <v-icon left small>mdi-check</v-icon>
            Import
          </v-btn>
        </template>
      </div>
    </div>

    <!-- File upload area (import mode only) -->
    <div
      v-if="currentMode === 'import'"
      class="json-export-import-manager__upload"
    >
      <v-file-input
        v-model="uploadedFile"
        accept=".json,application/json"
        :disabled="disabled"
        label="Upload JSON file"
        prepend-icon="mdi-paperclip"
        show-size
        truncate-length="50"
        @change="handleFileUpload"
        data-testid="file-upload"
      />
    </div>

    <!-- JSON Editor -->
    <div class="json-export-import-manager__editor">
      <div
        ref="editorContainer"
        class="json-export-import-manager__editor-container"
        :class="{
          'json-export-import-manager__editor-container--disabled': disabled,
          'json-export-import-manager__editor-container--readonly': currentMode === 'export'
        }"
      />
    </div>

    <!-- Status and validation messages -->
    <div class="json-export-import-manager__status">
      <div v-if="validationError" class="json-export-import-manager__error">
        <v-icon small color="error">mdi-alert-circle</v-icon>
        <span class="ml-1">{{ validationError }}</span>
      </div>
      <div v-else-if="hasValidJson" class="json-export-import-manager__success">
        <v-icon small color="success">mdi-check-circle</v-icon>
        <span class="ml-1">Valid JSON ({{ jsonStats }})</span>
      </div>
      <div v-else class="json-export-import-manager__info">
        <v-icon small color="info">mdi-information</v-icon>
        <span class="ml-1">{{ currentMode === 'export' ? 'JSON preview' : 'Enter or upload JSON configuration' }}</span>
      </div>
    </div>

    <!-- Success snackbar -->
    <v-snackbar
      v-model="showSuccessSnackbar"
      :timeout="3000"
      color="success"
      bottom
    >
      <v-icon left>mdi-check</v-icon>
      {{ successMessage }}
    </v-snackbar>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'

// CodeMirror imports
import CodeMirror from 'codemirror'
import 'codemirror/lib/codemirror.css'
import 'codemirror/mode/javascript/javascript'
import 'codemirror/addon/edit/matchbrackets'
import 'codemirror/addon/edit/closebrackets'
import 'codemirror/addon/selection/active-line'
import 'codemirror/addon/fold/foldcode'
import 'codemirror/addon/fold/foldgutter'
import 'codemirror/addon/fold/brace-fold'
import 'codemirror/addon/display/placeholder'
import 'codemirror/addon/lint/lint'
import 'codemirror/addon/lint/lint.css'
import 'codemirror/addon/lint/json-lint'
import 'codemirror/theme/material-darker.css'
import 'codemirror/theme/dracula.css'

export interface JsonExportImportData {
  [key: string]: unknown
}

export interface JsonExportImportManagerProps {
  value: string
  mode: 'export' | 'import'
  disabled: boolean
  filename: string
  theme: 'default' | 'material-darker' | 'dracula'
  placeholder: string
  readonly: boolean
}

export default Vue.extend({
  name: 'JsonExportImportManager',
  props: {
    value: {
      type: String,
      default: ''
    },
    mode: {
      type: String as () => 'export' | 'import',
      default: 'export',
      validator: (value: string) => ['export', 'import'].includes(value)
    },
    disabled: {
      type: Boolean,
      default: false
    },
    filename: {
      type: String,
      default: 'configuration'
    },
    theme: {
      type: String as () => 'default' | 'material-darker' | 'dracula',
      default: 'default',
      validator: (value: string) => ['default', 'material-darker', 'dracula'].includes(value)
    },
    placeholder: {
      type: String,
      default: 'Enter JSON configuration...'
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      editor: null as CodeMirror.Editor | null,
      currentMode: this.mode as 'export' | 'import',
      uploadedFile: null as File | null,
      validationError: '',
      showSuccessSnackbar: false,
      successMessage: '',
      isInitialized: false
    }
  },
  computed: {
    hasValidJson(): boolean {
      if (!this.currentJsonValue.trim()) return false
      try {
        JSON.parse(this.currentJsonValue)
        return true
      } catch {
        return false
      }
    },
    currentJsonValue(): string {
      return this.editor ? this.editor.getValue() : this.value
    },
    jsonStats(): string {
      if (!this.hasValidJson) return ''
      try {
        const parsed = JSON.parse(this.currentJsonValue)
        const keys = Object.keys(parsed).length
        const size = new Blob([this.currentJsonValue]).size
        return `${keys} properties, ${this.formatBytes(size)}`
      } catch {
        return ''
      }
    },
    editorReadonly(): boolean {
      return this.readonly || (this.currentMode === 'export' && !this.disabled)
    }
  },
  watch: {
    value: {
      handler(newValue: string) {
        if (this.editor && this.editor.getValue() !== newValue) {
          this.editor.setValue(newValue || '')
        }
      },
      immediate: false
    },
    currentMode: {
      handler(newMode: 'export' | 'import') {
        this.$emit('mode-change', newMode)
        this.updateEditorReadonly()
        this.clearValidation()
      }
    },
    theme: {
      handler() {
        this.updateEditorTheme()
      }
    }
  },
  mounted() {
    this.initializeEditor()
  },
  beforeDestroy() {
    if (this.editor) {
      // Clean up CodeMirror instance (matches JsonEditor pattern)
      const element = this.editor.getWrapperElement()
      if (element && element.parentNode) {
        element.parentNode.removeChild(element)
      }
      this.editor = null
    }
  },
  methods: {
    /**
     * Initialize CodeMirror editor
     */
    initializeEditor(): void {
      if (this.isInitialized || !this.$refs.editorContainer) return

      this.editor = CodeMirror(this.$refs.editorContainer as HTMLElement, {
        value: this.value || '',
        mode: 'application/json',
        theme: this.theme,
        lineNumbers: true,
        lineWrapping: true,
        matchBrackets: true,
        autoCloseBrackets: true,
        foldGutter: true,
        lint: true,
        placeholder: this.placeholder,
        readOnly: this.editorReadonly,
        gutters: ['CodeMirror-linenumbers', 'CodeMirror-foldgutter', 'CodeMirror-lint-markers'],
        extraKeys: {
          'Ctrl-Space': 'autocomplete',
          'Tab': (cm: CodeMirror.Editor) => {
            if (cm.somethingSelected()) {
              cm.indentSelection('add')
            } else {
              cm.replaceSelection('  ')
            }
          }
        }
      })

      this.setupEditorEvents()
      this.isInitialized = true
    },

    /**
     * Set up CodeMirror event listeners
     */
    setupEditorEvents(): void {
      if (!this.editor) return

      this.editor.on('change', () => {
        const value = this.editor!.getValue()
        this.$emit('input', value)
        this.validateJson()
      })

      this.editor.on('focus', () => {
        this.$emit('focus')
      })

      this.editor.on('blur', () => {
        this.$emit('blur')
      })
    },

    /**
     * Update editor readonly state
     */
    updateEditorReadonly(): void {
      if (this.editor) {
        this.editor.setOption('readOnly', this.editorReadonly)
      }
    },

    /**
     * Update editor theme
     */
    updateEditorTheme(): void {
      if (this.editor) {
        this.editor.setOption('theme', this.theme)
      }
    },

    /**
     * Validate JSON content
     */
    validateJson(): void {
      const value = this.currentJsonValue.trim()
      if (!value) {
        this.validationError = ''
        return
      }

      try {
        JSON.parse(value)
        this.validationError = ''
      } catch (error) {
        this.validationError = `Invalid JSON: ${(error as Error).message}`
      }
    },

    /**
     * Clear validation state
     */
    clearValidation(): void {
      this.validationError = ''
    },

    /**
     * Handle file upload
     */
    async handleFileUpload(file: File | null): Promise<void> {
      if (!file) return

      try {
        const text = await this.readFileAsText(file)
        
        // Validate JSON
        JSON.parse(text)
        
        // Set editor value
        if (this.editor) {
          this.editor.setValue(text)
        }
        
        this.showSuccess('File uploaded successfully')
        this.$emit('file-uploaded', { filename: file.name, content: text })
      } catch (error) {
        this.validationError = `Invalid JSON file: ${(error as Error).message}`
      }
    },

    /**
     * Read file as text
     */
    readFileAsText(file: File): Promise<string> {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => resolve(e.target?.result as string)
        reader.onerror = () => reject(new Error('Failed to read file'))
        reader.readAsText(file)
      })
    },

    /**
     * Copy JSON to clipboard
     */
    async copyToClipboard(): Promise<void> {
      if (!this.hasValidJson) return

      try {
        const formattedJson = this.formatJson(this.currentJsonValue)
        await navigator.clipboard.writeText(formattedJson)
        this.showSuccess('JSON copied to clipboard')
        this.$emit('copied', formattedJson)
      } catch (error) {
        this.validationError = 'Failed to copy to clipboard'
      }
    },

    /**
     * Download JSON as file
     */
    downloadJson(): void {
      if (!this.hasValidJson) return

      try {
        const formattedJson = this.formatJson(this.currentJsonValue)
        const blob = new Blob([formattedJson], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        
        const link = document.createElement('a')
        link.href = url
        link.download = `${this.filename}.json`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        URL.revokeObjectURL(url)
        
        this.showSuccess(`Downloaded ${this.filename}.json`)
        this.$emit('downloaded', { filename: `${this.filename}.json`, content: formattedJson })
      } catch (error) {
        this.validationError = 'Failed to download file'
      }
    },

    /**
     * Clear editor content
     */
    clearEditor(): void {
      if (this.editor) {
        this.editor.setValue('')
      }
      this.uploadedFile = null
      this.clearValidation()
      this.$emit('cleared')
    },

    /**
     * Import configuration
     */
    importConfiguration(): void {
      if (!this.hasValidJson) return

      try {
        const parsed = JSON.parse(this.currentJsonValue)
        this.showSuccess('Configuration imported successfully')
        this.$emit('import', parsed)
      } catch (error) {
        this.validationError = `Import failed: ${(error as Error).message}`
      }
    },

    /**
     * Format JSON string
     */
    formatJson(jsonString: string): string {
      try {
        const parsed = JSON.parse(jsonString)
        return JSON.stringify(parsed, null, 2)
      } catch {
        return jsonString
      }
    },

    /**
     * Format bytes to human readable string
     */
    formatBytes(bytes: number): string {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
    },

    /**
     * Show success message
     */
    showSuccess(message: string): void {
      this.successMessage = message
      this.showSuccessSnackbar = true
    }
  }
})
</script>

<style lang="scss" scoped>
.json-export-import-manager {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
  }

  &__mode-toggle {
    .v-btn-toggle {
      border: 1px solid #e0e0e0;
      border-radius: var(--radius-sm);
    }
  }

  &__actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__upload {
    border: 2px dashed #e0e0e0;
    border-radius: var(--radius);
    padding: 16px;
    background-color: #fafafa;
    transition: border-color 0.2s ease;

    &:hover {
      border-color: #2196f3;
    }
  }

  &__editor {
    flex: 1;
    min-height: 300px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    overflow: hidden;

    &-container {
      height: 100%;
      min-height: 300px;

      &--disabled {
        opacity: 0.6;
        pointer-events: none;
      }

      &--readonly {
        background-color: #f5f5f5;
      }
    }
  }

  &__status {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 14px;
  }

  &__error {
    color: var(--danger);
    background-color: var(--danger-light);
    border: 1px solid var(--danger-border);
  }

  &__success {
    color: #388e3c;
    background-color: #e8f5e8;
    border: 1px solid #c8e6c9;
  }

  &__info {
    color: #1976d2;
    background-color: #e3f2fd;
    border: 1px solid #bbdefb;
  }
}

// CodeMirror styling
.json-export-import-manager :deep(.CodeMirror) {
  height: 100%;
  min-height: 300px;
  font-family: 'Roboto Mono', 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 14px;
  line-height: 1.4;
}

.json-export-import-manager :deep(.CodeMirror-line) {
  padding-left: 8px;
}

.json-export-import-manager :deep(.CodeMirror-gutters) {
  border-right: 1px solid #e0e0e0;
  background-color: #f5f5f5;
}

.json-export-import-manager :deep(.CodeMirror-linenumber) {
  color: #999;
  padding: 0 8px;
}

.json-export-import-manager :deep(.CodeMirror-cursor) {
  border-left: 2px solid #2196f3;
}

.json-export-import-manager :deep(.CodeMirror-focused .CodeMirror-cursor) {
  border-left: 2px solid #1976d2;
}

.json-export-import-manager :deep(.CodeMirror-selected) {
  background-color: #e3f2fd;
}

.json-export-import-manager :deep(.CodeMirror-lint-marker-error) {
  background-image: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTggMTVBNyA3IDAgMSAxIDggMUE3IDcgMCAwIDEgOCAxNVpNOC41IDVIOC41VjlIOC41VjVaTTggMTFBMSAxIDAgMSAwIDggMTNBMSAxIDAgMCAwIDggMTFaIiBmaWxsPSIjRDMyRjJGIi8+Cjwvc3ZnPgo=");
  background-repeat: no-repeat;
  background-position: center;
  width: 16px;
  height: 16px;
}

// Theme-specific overrides
.json-export-import-manager :deep(.cm-s-material-darker) {
  .CodeMirror-gutters {
    background-color: #263238;
    border-right: 1px solid #37474f;
  }
  
  .CodeMirror-linenumber {
    color: #546e7a;
  }
}

.json-export-import-manager :deep(.cm-s-dracula) {
  .CodeMirror-gutters {
    background-color: #282a36;
    border-right: 1px solid #44475a;
  }
  
  .CodeMirror-linenumber {
    color: #6272a4;
  }
}
</style>
